import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SilaWalletCard } from '@/components/wallet/SilaWalletCard';
import { Leaf, TicketPercent, ArrowRight, Sprout, Award, Wallet } from 'lucide-react';

const BOX_IMG = '/sila/tessera-cervo.jpg';

const CHIPS = [
  { Icon: TicketPercent, text: 'Sconti crescenti' },
  { Icon: Wallet, text: 'Risparmi' },
  { Icon: Sprout, text: 'Punti a ogni acquisto' },
  { Icon: Leaf, text: 'Sei un turista attivista' },
];

const LEDGER = [
  { value: '€ 24.800', label: 'destinati al Progetto', testid: 'home-sustainability-impact-metric-1' },
  { value: '12', label: 'progetti sostenuti', testid: 'home-sustainability-impact-metric-2' },
  { value: '3%', label: 'di ogni acquisto', testid: 'home-sustainability-impact-metric-3' },
];

const TIER_CARDS = [
  { tier: 'bronze', label: 'Conoscenza', note: 'Il tuo primo livello', points: 320, card: 'SILA-CO-2048' },
  { tier: 'silver', label: 'Passione', note: 'Vantaggi dedicati', points: 780, card: 'SILA-PA-5109' },
  { tier: 'gold', label: 'Connessione', note: 'Massimo riconoscimento', points: 1720, card: 'SILA-CN-2C90' },
];

/**
 * Home — "Come funziona" / Sila Experiences Benefits: box foto (cervo del Parco)
 * + 3 tessere che ruotano (flip) al passaggio del mouse.
 * NB: le tessere sono LIVELLI di fedeltà, senza percentuali di sconto crescenti.
 */
export const SustainabilityMegaBanner = () => (
  <section id="come-funziona" className="sila-section py-14 sm:py-20" aria-labelledby="tessera-heading">
    {/* Box foto + gradiente */}
    <div
      data-testid="home-sustainability-mega-banner"
      className="relative isolate overflow-hidden rounded-3xl border border-border shadow-[0_24px_80px_hsla(150,30%,12%,0.28)]"
    >
      <img
        src={BOX_IMG}
        alt="Cervo nel bosco autunnale del Parco Nazionale della Sila"
        className="absolute inset-0 -z-10 h-full w-full object-cover"
        style={{ transform: 'scaleX(-1)', objectPosition: 'center' }}
        loading="lazy"
      />
      {/* Gradiente pulito: scuro a sinistra (testo) -> immagine visibile a destra */}
      <div aria-hidden className="absolute inset-0 -z-10" style={{ background: 'linear-gradient(180deg, rgba(10,16,12,0.52) 0%, rgba(10,16,12,0.68) 100%)' }} />
      <div aria-hidden className="absolute inset-0 -z-10" style={{ background: 'linear-gradient(90deg, rgba(9,15,11,0.92) 0%, rgba(9,15,11,0.66) 44%, rgba(9,15,11,0.18) 78%, rgba(9,15,11,0) 100%)' }} />

      <div className="relative max-w-2xl px-7 py-10 text-[hsl(36_33%_96%)] sm:px-12 sm:py-14">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide backdrop-blur-sm">
          <Award className="h-3.5 w-3.5" style={{ color: 'hsl(var(--brand-sun))' }} />Sila Experiences Benefits
        </span>
        <h2 id="tessera-heading" className="mt-5 font-display text-4xl font-bold leading-[1.04] tracking-tight sm:text-5xl lg:text-[3.3rem]">
          Mentre tu ti diverti,<br className="hidden sm:block" /> risparmi e la Sila cresce!
        </h2>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/85 sm:text-base">
          Registrandoti all'App <span className="font-semibold text-white">Sila Experiences</span> ogni acquisto ha una
          convenzione esclusiva di sconto dedicata solo a te. Una quota di ogni tuo acquisto sarà devoluta al Progetto
          di sviluppo dell'app Sila Experiences. Nel carrello vedrai in chiaro il prezzo intero, lo sconto e quanto sarà
          devoluto al progetto di sviluppo del territorio.
        </p>

        <div className="mt-6 flex flex-wrap gap-2.5">
          {CHIPS.map(({ Icon, text }) => (
            <span key={text} className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-sm text-white/90 backdrop-blur-sm">
              <Icon className="h-4 w-4" style={{ color: 'hsl(var(--brand-sun))' }} />{text}
            </span>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Button asChild size="lg" className="group rounded-full" data-testid="home-sustainability-primary-cta">
            <Link to="/cliente/registrati" className="flex items-center">
              Iscriviti all'app Sila Experiences
              <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white" data-testid="home-sustainability-secondary-cta">
            <Link to="/il-progetto">Come funziona il contributo</Link>
          </Button>
        </div>

        <div className="mt-8 grid max-w-lg grid-cols-3 gap-3 border-t border-white/15 pt-6">
          {LEDGER.map(({ value, label, testid }) => (
            <div key={label} data-testid={testid}>
              <p className="font-display text-xl font-bold leading-none tabular-nums sm:text-2xl" style={{ color: 'hsl(var(--brand-sun))' }}>{value}</p>
              <p className="mt-1.5 text-[11px] leading-tight text-white/75">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Intro tessere (testo PDF "Sila Experiences Cards") */}
    <div className="mx-auto mt-14 max-w-3xl text-center">
      <h3 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Le card Sila Experiences</h3>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
        Ad ogni spesa accumuli punti nel tuo wallet, che potrai convertire in premi o sconti. Più la tua vacanza è
        sostenibile e le tue scelte responsabili, green ed etiche, più cresceranno i tuoi punti e crescerai di livello:
        Conoscenza, Passione e Connessione.
      </p>
    </div>

    {/* Le 3 tessere — livelli di fedelta' */}
    <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-tessera-cards">
      {TIER_CARDS.map((tc) => (
        <div key={tc.tier} className="tessera-lift space-y-3" data-testid={`home-tessera-card-${tc.tier}`}>
          <SilaWalletCard tier={tc.tier} name="Cliente Sila" cardNumber={tc.card} points={tc.points} shimmer={tc.tier === 'gold'} interactive={false} title="Card Sila" />
          <div className="flex items-center justify-between px-1">
            <span className="font-display text-base font-semibold text-foreground">{tc.label}</span>
            <span className="text-sm text-muted-foreground">{tc.note}</span>
          </div>
        </div>
      ))}
    </div>
    <p className="mt-3 text-center text-xs text-muted-foreground">Passa il mouse su una card per girarla e vederne il retro.</p>
  </section>
);

export default SustainabilityMegaBanner;
